'use client';

import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useFirestore } from '@/firebase';
import { getActiveSale, hasLiveCountrySale, sanitizeSaleConfig, type SaleEvent } from '@/lib/sale-config';
import { useVisitorCountry } from '@/hooks/use-visitor-country';

/**
 * The sale running right now, for every surface that advertises one (shop,
 * lobby, homepage, games list). `config/sale` is publicly readable so this
 * works for guests too.
 *
 * Display only: the discount itself is applied server-side when the Paddle
 * transaction is created, so a stale or tampered client can never change what
 * somebody is actually charged.
 *
 * Country sales only show to visitors from their countries (see
 * useVisitorCountry); everyone else sees the worldwide sale, if any.
 *
 * The default tick is slow on purpose. Banners show a coarse "6d 11h", so
 * there is nothing to gain from re-rendering a live lobby once a second.
 */
export function useActiveSale(tickMs = 30_000): { sale: SaleEvent | null; nowMs: number } {
    const firestore = useFirestore();
    const [sales, setSales] = useState<SaleEvent[]>([]);
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        if (!firestore) return;
        const unsub = onSnapshot(
            doc(firestore, 'config', 'sale'),
            (snap) => setSales(sanitizeSaleConfig(snap.data()).sales),
            // A failure here only costs this user the banner, never the sale
            // price itself, so it stays a warning rather than an admin report.
            (error) => console.warn('Sale config unavailable:', error.message)
        );
        return () => unsub();
    }, [firestore]);

    useEffect(() => {
        const timer = setInterval(() => setNowMs(Date.now()), tickMs);
        return () => clearInterval(timer);
    }, [tickMs]);

    const country = useVisitorCountry(hasLiveCountrySale(sales, nowMs));
    const sale = useMemo(() => getActiveSale(sales, nowMs, country), [sales, nowMs, country]);
    return { sale, nowMs };
}
